import { Fragment, type ReactNode } from "react";

const COLOR_CLASSES: { [sourceClass: string]: string } = {
  "text-red": "text-red-700",
  "text-amber": "text-amber-700",
  "text-green": "text-green-700",
};

const colorClassFor = (element: Element): string | undefined => {
  const sourceClass = (element.getAttribute("class") ?? "")
    .split(/\s+/)
    .find((c) => c in COLOR_CLASSES);
  return sourceClass ? COLOR_CLASSES[sourceClass] : undefined;
};

/**
 * Renders question header/description text, which may contain a small set of
 * formatting tags (bold/italic/paragraphs/line breaks) and `<span
 * class="text-red|text-amber|text-green">` color markers. Only a fixed
 * allow-list of tags/classes is ever turned into real elements; anything else
 * is unwrapped to its text content, so no attribute or tag can reach the DOM
 * unfiltered.
 */
export const renderQuestionText = (html: string | null | undefined): ReactNode => {
  if (!html) {
    return null;
  }
  const root = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html").body
    .firstElementChild;
  if (!root) {
    return null;
  }
  return renderNodes(root.childNodes);
};

const renderNodes = (nodes: NodeListOf<ChildNode>): ReactNode => {
  return Array.from(nodes).map((node, index) => (
    <Fragment key={index}>{renderNode(node)}</Fragment>
  ));
};

const renderNode = (node: ChildNode): ReactNode => {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }
  const element = node as Element;
  const children = renderNodes(element.childNodes);
  switch (element.tagName) {
    case "BR":
      return <br />;
    case "SPAN":
      return <span className={colorClassFor(element)}>{children}</span>;
    case "DIV":
      return <div className={colorClassFor(element)}>{children}</div>;
    case "P":
      return <p className={colorClassFor(element)}>{children}</p>;
    case "STRONG":
    case "B":
      return <strong>{children}</strong>;
    case "EM":
    case "I":
      return <em>{children}</em>;
    case "U":
      return <u>{children}</u>;
    default:
      return children;
  }
};
